/**
 * Pass / fail / warning indicator.
 *
 * Status is never signalled by colour alone: each carries an icon and a text label,
 * for colour-vision deficiency and for the greyscale printing that calculation sheets
 * routinely get (CLAUDE.md 14.2).
 */
import { AlertTriangle, CheckCircle2, MinusCircle, XCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/cn'

export type Status = 'pass' | 'fail' | 'warning' | 'not_applicable'

const STYLES: Record<Status, { icon: typeof CheckCircle2; className: string; key: string }> = {
  pass: {
    icon: CheckCircle2,
    className: 'text-[var(--status-pass)] bg-[var(--status-pass-bg)]',
    key: 'status.pass',
  },
  fail: {
    icon: XCircle,
    className: 'text-[var(--status-fail)] bg-[var(--status-fail-bg)]',
    key: 'status.fail',
  },
  warning: {
    icon: AlertTriangle,
    className: 'text-[var(--status-warn)] bg-[var(--status-warn-bg)]',
    key: 'status.warning',
  },
  not_applicable: {
    icon: MinusCircle,
    className: 'text-[var(--status-na)] bg-[var(--status-na-bg)]',
    key: 'status.not_applicable',
  },
}

export function StatusBadge({ status, className }: { status: Status; className?: string }) {
  const { t } = useTranslation()
  const style = STYLES[status] ?? STYLES.not_applicable
  const Icon = style.icon

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded px-2 py-1 text-sm font-medium',
        style.className,
        className,
      )}
    >
      <Icon size={15} aria-hidden />
      {t(style.key)}
    </span>
  )
}
